/**
 * A remark plugin for agent replies: every complete id (a UUID) in the text,
 * written plainly or in `backticks`, becomes a link the renderer can turn
 * into something useful, today a workflow's name linking to its canvas.
 *
 * Only a full 36-character id matches, so while a reply streams, a half-typed
 * id stays plain text until the rest of it arrives. Ids already inside a link
 * (a URL) are left alone. The renderer decides what each id is; one that is
 * not a workflow shows exactly as written.
 */

/** The marker href; a fragment, so the markdown URL filter keeps it. */
export const UUID_LINK_PREFIX = "#proclaw-id-";

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const SPLIT = new RegExp(`(?<![0-9a-f-])(${UUID})(?![0-9a-f-])`, "i");
const WHOLE = new RegExp(`^${UUID}$`, "i");

interface MdNode {
  type: string;
  value?: string;
  url?: string;
  title?: string | null;
  children?: MdNode[];
}

/** `title` carries how the id was written, so it can be shown the same way. */
function idLink(id: string, writtenAs: "code" | "text"): MdNode {
  return {
    type: "link",
    url: `${UUID_LINK_PREFIX}${id}`,
    title: writtenAs,
    children: [{ type: "text", value: id }],
  };
}

function walk(node: MdNode) {
  if (!node.children || node.type === "link" || node.type === "linkReference") return;
  const next: MdNode[] = [];
  for (const child of node.children) {
    if (child.type === "inlineCode" && child.value && WHOLE.test(child.value.trim())) {
      next.push(idLink(child.value.trim(), "code"));
    } else if (child.type === "text" && child.value && SPLIT.test(child.value)) {
      // split() with a capture group keeps the ids at the odd positions.
      child.value.split(new RegExp(SPLIT.source, "gi")).forEach((part, i) => {
        if (!part) return;
        next.push(i % 2 === 1 ? idLink(part, "text") : { type: "text", value: part });
      });
    } else {
      walk(child);
      next.push(child);
    }
  }
  node.children = next;
}

export function remarkUuidLinks() {
  return (tree: MdNode) => walk(tree);
}
